import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PublicScanModelVisibility } from '@brand24-original/llm-types';
import ReportSection from './ReportSection';
import { modelLabel } from './modelLabel';
import './ReportPlatformPerformance.scss';

interface ReportPlatformPerformanceProps {
  models: PublicScanModelVisibility[];
}

/** Znaczniki podziałki pod paskami — skala jest zawsze pełna, 0-100%. */
const SCALE_TICKS = [0, 25, 50, 75, 100];

export const ReportPlatformPerformance: React.FC<ReportPlatformPerformanceProps> = ({ models }) => {
  const { t } = useTranslation();

  const valueOf = (entry: PublicScanModelVisibility) => entry.visibility;

  const ranked = useMemo(
    () => [...models].sort((a, b) => valueOf(b) - valueOf(a)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [models]
  );

  // Przy samych zerach „dominujący" i „najsłabszy" model nic nie znaczą — każdy jest tak samo pusty.
  const hasVisibility = ranked.some((entry) => valueOf(entry) > 0);
  const dominant = ranked[0];
  const weakest = ranked.length > 1 ? ranked[ranked.length - 1] : null;
  // Gdy wszystkie modele mają tę samą wartość, podział na najlepszy i najgorszy jest pozorny.
  const isEven = !!weakest && valueOf(dominant) === valueOf(weakest);

  // Slajd bez danych w ogóle nie wchodzi do raportu — ani na stronie, ani w PDF.
  if (ranked.length === 0) {
    return null;
  }

  const metrics = (
    <>
      <ul className="report-platform__bars">
        {ranked.map((entry) => (
          <li key={entry.model} className="report-platform__bar-row">
            <span className="report-platform__bar-name">{modelLabel(entry.model)}</span>
            <div className="report-section__track">
              <div
                className="report-section__bar"
                style={{ width: `${Math.min(valueOf(entry), 100)}%` }}
              />
            </div>
            <span className="report-platform__bar-value">{valueOf(entry)}%</span>
          </li>
        ))}
      </ul>

      <div className="report-platform__scale">
        <div className="report-platform__scale-ticks">
          {SCALE_TICKS.map((tick) => (
            <span key={tick}>{tick}%</span>
          ))}
        </div>
      </div>
    </>
  );

  // Bez kafli metryki zajmują całą sekcję — zostaje sam nagłówek, bez ramki wokół.
  if (!hasVisibility) {
    return (
      <ReportSection
        title={t('reportPlatformTitle')}
        titleAccent={t('reportPlatformTitleAccent')}
      >
        <h3 className="report-section__panel-title">{t('reportPlatformMetricsTitle')}</h3>
        <p className="report-section__panel-desc">{t('reportPlatformMetricsDesc')}</p>
        {metrics}
      </ReportSection>
    );
  }

  return (
    <ReportSection
      title={t('reportPlatformTitle')}
      titleAccent={t('reportPlatformTitleAccent')}
      aside={
        <div className="report-platform__metrics report-section__panel">
          <h3 className="report-section__panel-title">{t('reportPlatformMetricsTitle')}</h3>
          <p className="report-section__panel-desc">{t('reportPlatformMetricsDesc')}</p>
          {metrics}
        </div>
      }
    >
      <div className="report-platform__models">
        {isEven ? (
          <div className="report-platform__model report-platform__model--dominant">
            <span className="report-platform__model-label">{t('reportPlatformEven')}</span>
            <strong className="report-platform__model-name">{valueOf(dominant)}%</strong>
            <span className="report-platform__model-share">
              {t('reportPlatformEvenDesc', { count: ranked.length })}
            </span>
          </div>
        ) : (
          <>
            {dominant && (
              <div className="report-platform__model report-platform__model--dominant">
                <span className="report-platform__model-label">{t('reportPlatformDominant')}</span>
                <strong className="report-platform__model-name">{modelLabel(dominant.model)}</strong>
                <span className="report-platform__model-share">
                  {t('reportPlatformShare', { value: valueOf(dominant) })}
                </span>
              </div>
            )}
            {weakest && (
              <div className="report-platform__model report-platform__model--weakest">
                <span className="report-platform__model-label">{t('reportPlatformWeakest')}</span>
                <strong className="report-platform__model-name">{modelLabel(weakest.model)}</strong>
                <span className="report-platform__model-share">
                  {t('reportPlatformShare', { value: valueOf(weakest) })}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </ReportSection>
  );
};

export default ReportPlatformPerformance;
